import { X } from "lucide-react";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface OffCanvasProps {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  subtitle?: ReactNode;
  footer?: ReactNode;
  headerExtra?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  hideCloseButton?: boolean;
  /** "elevated" empilha acima de outro OffCanvas já aberto. */
  zLevel?: "base" | "elevated";
  /** Cor de fundo do header. "gray" alinha com o cinza das seções internas. */
  headerVariant?: "white" | "gray";
}

export function OffCanvas({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  headerExtra,
  size = "md",
  hideCloseButton = false,
  zLevel = "base",
  headerVariant = "white",
}: OffCanvasProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // `mounted` controla se o portal está no DOM.
  // `visible` controla a classe CSS de transição.
  const [mounted, setMounted] = useState(isOpen);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // A MONTAGEM não pode depender de requestAnimationFrame. O navegador não
      // executa callbacks de rAF quando a aba não está pintando (janela em
      // segundo plano, minimizada, renderer throttled). Com o mount dentro do
      // rAF, `isOpen` virava true e o portal nunca entrava no DOM: o painel
      // simplesmente não abria, sem erro nenhum no console. Só o `visible`
      // (classe de transição) fica no rAF, porque animação sem pintura não faz
      // sentido mesmo — e se o frame não vier, o painel aparece sem o slide.
      setMounted(true);
      const rafId = requestAnimationFrame(() => setVisible(true));
      // Rede de segurança pelo mesmo motivo: sem pintura o rAF não roda, o
      // `visible` nunca vira true e o painel fica montado porém transladado para
      // fora da tela — invisível e sem receber clique ou digitação. O timeout
      // garante a abertura; quando o rAF vem primeiro, isto é inofensivo porque
      // setVisible(true) sobre true não re-renderiza.
      const visibleFallbackId = setTimeout(() => setVisible(true), 50);
      document.body.style.overflow = "hidden";
      return () => {
        cancelAnimationFrame(rafId);
        clearTimeout(visibleFallbackId);
        document.body.style.overflow = "";
      };
    } else {
      const rafId = requestAnimationFrame(() => setVisible(false));
      document.body.style.overflow = "";
      // Remove do DOM somente após a transição de saída terminar (300 ms)
      const timer = setTimeout(() => setMounted(false), 300);
      return () => {
        cancelAnimationFrame(rafId);
        clearTimeout(timer);
      };
    }
  }, [isOpen]);

  // Trap de foco dentro do painel
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen || !panelRef.current) return;

      if (e.key === "Escape") {
        if (!hideCloseButton) onClose();
        return;
      }

      if (e.key === "Tab") {
        const focusable = panelRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    },
    [isOpen, onClose, hideCloseButton]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Foco inicial no painel ao abrir
  useEffect(() => {
    if (isOpen && panelRef.current) {
      const first = panelRef.current.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      first?.focus();
    }
  }, [isOpen]);

  const sizeMap = {
    sm: "max-w-[400px]",
    md: "max-w-[650px]",
    lg: "max-w-[800px]",
    xl: "max-w-[1000px]",
  };

  if (!mounted) return null;

  const backdropZ = zLevel === "elevated" ? "z-[110]" : "z-[90]";
  const panelZ = zLevel === "elevated" ? "z-[120]" : "z-[100]";

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 ${backdropZ} bg-black/50 transition-opacity duration-300 ${
          visible ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        aria-hidden="true"
      />

      {/* Off-canvas Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="offcanvas-title"
        className={`fixed top-0 right-0 ${panelZ} flex h-full flex-col bg-white ${
          sizeMap[size]
        } w-full transition-transform duration-300 ease-in-out ${
          visible ? "translate-x-0 shadow-2xl" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div
          className={`flex flex-col border-b border-gray-200 px-6 py-4 ${
            headerVariant === "gray" ? "bg-gray-50" : "bg-white"
          }`}
        >
          <div className="flex items-center justify-between">
            <h2
              id="offcanvas-title"
              className="text-lg font-semibold text-gray-900"
            >
              {title}
            </h2>
            {!hideCloseButton && (
              <button
                onClick={onClose}
                className="rounded-lg p-1 transition-colors hover:bg-gray-100"
                aria-label="Fechar"
              >
                <X size={20} className="text-gray-600" />
              </button>
            )}
          </div>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>

        {/* Header Extra (e.g. tabs) */}
        {headerExtra}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </>,
    document.body
  );
}
